/** @jsx jsx */
import React, { useRef } from 'react';
import { createPortal } from 'react-dom';
import { css, jsx } from '@compiled/react';
import Avatar, { AvatarItem } from '@atlaskit/avatar';
import { DragHandleButton } from '@atlaskit/pragmatic-drag-and-drop-react-accessibility/drag-handle-button';
import { DropIndicator } from '@atlaskit/pragmatic-drag-and-drop-react-drop-indicator/box';
import { Box, Inline, xcss } from '@atlaskit/primitives';
import { useIntl } from '@atlassian/jira-intl';
import type { ParticipantProps } from '../../../common/types';
import { useParticipantDragAndDrop } from '../../../controllers/drag-and-drop/participant';
import { useStandupStore } from '../../../controllers/store';
import { ItemActivity } from './activity';
import messages from './messages';

export const PARTICIPANT_ITEM_TEST_ID = 'rituals.standups.participant.item';

export const ParticipantItem = ({ id, name, avatar, status }: ParticipantProps) => {
	const { formatMessage } = useIntl();
	const participantRef = useRef<HTMLDivElement>(null);
	const dragHandleRef = useRef<HTMLButtonElement>(null);

	const [{ currentlyFilteredId }, { toggleFilteredParticipant }] = useStandupStore();
	const { closestEdge, draggableState } = useParticipantDragAndDrop({
		participantRef,
		dragHandleRef,
		id,
		name,
		status,
	});

	return (
		<div css={parentStyles} data-testid={PARTICIPANT_ITEM_TEST_ID} ref={participantRef}>
			<Inline
				alignBlock="center"
				grow="fill"
				xcss={[outerWrapperStyles, currentlyFilteredId === id && currentlyFilteredStyles]}
			>
				<Box xcss={draghandleStyles}>
					{status !== 'unassigned' && (
						<DragHandleButton
							appearance="subtle"
							ref={dragHandleRef}
							label={formatMessage(messages.reorderParticipantNonfinal)}
						/>
					)}
				</Box>
				<Inline grow="fill" xcss={nameWrapperStyles}>
					<AvatarItem
						primaryText={name}
						avatar={
							<Avatar onClick={() => toggleFilteredParticipant(id)} src={avatar} name={name} />
						}
					/>
				</Inline>
				<Box xcss={buttonWrapperStyles}>
					<ItemActivity id={id} status={status} />
				</Box>
				{closestEdge && <DropIndicator edge={closestEdge} />}
			</Inline>
			{draggableState.type === 'preview' &&
				createPortal(
					<Box xcss={listItemPreviewStyles}>
						<AvatarItem primaryText={name} avatar={<Avatar src={avatar} name={name} />} />
					</Box>,
					draggableState.container,
				)}
		</div>
	);
};

// Can't set CSS variables via xcss, figure out later if we do it this way or via mouse events and UI state
const parentStyles = css({
	'--child-hover-opacity': 0,

	'&:hover, &:focus, &:focus-within': {
		'--child-hover-opacity': 1,
	},
});

const outerWrapperStyles = xcss({
	backgroundColor: 'color.background.neutral.subtle',
	paddingRight: 'space.100',
	position: 'relative',
	border: '0 solid transparent',
	borderWidth: '0 0 0 2px',
	transition: 'background 0.2s ease-in-out',

	':hover': {
		backgroundColor: 'color.background.neutral.subtle.hovered',
	},

	':focus': {
		backgroundColor: 'color.background.neutral.subtle.hovered',
	},
});

const currentlyFilteredStyles = xcss({
	borderColor: 'color.border.selected',
	backgroundColor: 'color.background.selected',

	':hover': {
		borderColor: 'color.border.selected',
		backgroundColor: 'color.background.selected.hovered',
	},

	':focus': {
		borderColor: 'color.border.selected',
		backgroundColor: 'color.background.selected.hovered',
	},
});

const draghandleStyles = xcss({
	width: '18px',
	opacity: 'var(--child-hover-opacity, 0)',
	transition: 'opacity 0.2s ease-in-out',
});

const nameWrapperStyles = xcss({
	maxWidth: 'calc(100% - 18px - 32px)',
});

const buttonWrapperStyles = xcss({
	justifySelf: 'flex-end',
	opacity: 'var(--child-hover-opacity, 0)',
	transition: 'opacity 0.2s ease-in-out',
});

const listItemPreviewStyles = xcss({
	paddingInline: 'space.100',
	borderRadius: 'border.radius.100',
	backgroundColor: 'elevation.surface.overlay',
	maxWidth: '180px',
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	transform: 'rotate(-5deg)',
});
